import { Dispatch, SetStateAction, useState } from "react";
import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tooltip,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { scaleCanvas } from "@store/slices/workspaceSlice";
import {
  selectViewedCanvasScale,
  selectViewedRectangles,
} from "@store/selectors";
import { setStagePosition } from "@features/annogram/canvas/state/canvasAuxSlice";
import {
  fitTransform,
  visibleCanvasViewport,
} from "@features/annogram/canvas/utils/fitView";

interface Props {
  scale: string;
  setScale: Dispatch<SetStateAction<string>>;
  canvasOn: boolean;
}

const READER_SCALE_OPTIONS_INITIAL = [
  { label: "Actual Size", value: "page-actual" },
  { label: "Page Width", value: "page-width" },
  { label: "Fit Page", value: "page-fit" },
  { label: "Auto", value: "auto" },
  { label: "50%", value: "0.50" },
  { label: "100%", value: "1" },
  { label: "150%", value: "1.50" },
  { label: "200%", value: "2" },
];

const ViewZoomToolbar = ({ scale, setScale, canvasOn }: Props) => {
  const dispatch = useAppDispatch();
  const canvasScale = useAppSelector(selectViewedCanvasScale);
  const rectangles = useAppSelector(selectViewedRectangles);

  const [readerScaleOptions, setReaderScaleOptions] = useState(
    READER_SCALE_OPTIONS_INITIAL
  );
  // Derived, not stored: the current scale is always an option, so the
  // Select never holds a value missing from its list (which renders as
  // a blank control) — regardless of which code path changed the scale.
  const canvasScaleOptions = [
    { label: `${(canvasScale * 100).toFixed()}%`, value: canvasScale },
    ...(canvasScale === 2 ? [] : [{ label: "Maximum", value: 2 }]),
    ...(canvasScale === 0.1 ? [] : [{ label: "Minimum", value: 0.1 }]),
  ];

  const handleScaleSelect = (event: SelectChangeEvent) => {
    if (canvasOn) {
      dispatch(scaleCanvas({ scale: parseFloat(event.target.value) }));
    } else {
      setScale(event.target.value);
    }
  };

  const handleScaleIncrease = () => {
    if (canvasOn && canvasScale < 2) {
      const newValue = Math.round((canvasScale + 0.05) * 100) / 100;
      dispatch(scaleCanvas({ scale: newValue }));
    } else if (!canvasOn) {
      if (
        scale === "page-actual" ||
        scale === "page-width" ||
        scale === "page-fit" ||
        scale === "auto"
      ) {
        setScale("1");
      } else {
        const newScaleOptions = readerScaleOptions.filter(
          (option) =>
            option.value === "page-actual" ||
            option.value === "page-width" ||
            option.value === "page-fit" ||
            option.value === "auto" ||
            parseFloat(option.value) > parseFloat(scale)
        );
        setReaderScaleOptions([
          ...newScaleOptions,
          {
            label: `${((parseFloat(scale) + 0.05) * 100).toFixed()}%`,
            value: (parseFloat(scale) + 0.05).toFixed(2).toString(),
          },
        ]);
        setScale((parseFloat(scale) + 0.05).toFixed(2).toString());
      }
    }
  };

  const handleScaleDecrease = () => {
    if (canvasOn && canvasScale > 0.1) {
      const newValue = Math.round((canvasScale - 0.05) * 100) / 100;
      dispatch(scaleCanvas({ scale: newValue }));
    } else if (!canvasOn) {
      if (
        scale === "page-actual" ||
        scale === "page-width" ||
        scale === "page-fit" ||
        scale === "auto"
      ) {
        setScale("1");
      } else if (parseFloat(scale) > 0.1) {
        const newScaleOptions = readerScaleOptions.filter(
          (option) =>
            option.value === "page-actual" ||
            option.value === "page-width" ||
            option.value === "page-fit" ||
            option.value === "auto" ||
            parseFloat(option.value) > parseFloat(scale)
        );
        setReaderScaleOptions([
          ...newScaleOptions,
          {
            label: `${((parseFloat(scale) - 0.05) * 100).toFixed()}%`,
            value: (parseFloat(scale) - 0.05).toFixed(2).toString(),
          },
        ]);
        setScale((parseFloat(scale) - 0.05).toFixed(2).toString());
      }
    }
  };

  const handleFitToContent = () => {
    const fit = fitTransform(rectangles, visibleCanvasViewport());
    if (!fit) return;
    dispatch(scaleCanvas({ scale: fit.scale }));
    dispatch(setStagePosition(fit.position));
  };

  return (
    <Paper
      elevation={2}
      aria-label="Zoom controls"
      sx={{
        position: "fixed",
        bottom: 16,
        right: 16,
        zIndex: 10,
        px: 1,
        py: 0.5,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        border: 1,
        borderColor: "divider",
      }}
    >
      <Tooltip title="Zoom Out">
        <IconButton onClick={handleScaleDecrease} size="small">
          <ZoomOutIcon />
        </IconButton>
      </Tooltip>
      <FormControl sx={{ minWidth: 100 }} size="small">
        <InputLabel id="select-zoom">Zoom</InputLabel>
        <Select
          labelId="select-zoom"
          id="select-zoom"
          value={canvasOn ? canvasScale.toString() : scale}
          label="Zoom"
          onChange={handleScaleSelect}
        >
          {canvasOn
            ? canvasScaleOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))
            : readerScaleOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
        </Select>
      </FormControl>
      <Tooltip title="Zoom In">
        <IconButton onClick={handleScaleIncrease} size="small">
          <ZoomInIcon />
        </IconButton>
      </Tooltip>
      {canvasOn && (
        <Tooltip title="Fit all nodes in view">
          <IconButton
            onClick={handleFitToContent}
            size="small"
            aria-label="Fit all nodes in view"
          >
            <FitScreenIcon />
          </IconButton>
        </Tooltip>
      )}
    </Paper>
  );
};

export default ViewZoomToolbar;
